import React from 'react';
import { TextButton, IconButton } from '../Button';
import { SystemIcons } from '../icons';
import { COLORS, ComponentL, ComponentM, ComponentTextStyle, ComponentXL } from '../styles';
import { Size } from '../types';
import ModalContainer from './ModalContainer';
import {
  BackButtonWrapper,
  CloseButtonWrapper,
  Column,
  FlexContainer,
  ModalBody,
  ModalFooter,
  ModalHeaderActions,
  ModalHeaderActionsWithImage,
  ModalTitleSection,
  StyledModalHeader,
  ModalHoverModifier,
  ImageOverlay,
  ImageContainer,
} from './ModalStyles';

import { ModalNote } from './ModalNote';
import { TooltipWrapper } from '../Tooltips';
import { ButtonAction, LeftFooterAction, LeftFooterButton, LeftFooterHyperlink } from './ModalTypes';
import { HyperLink, ImageWithFallbacks } from '..';
import { ImageWithFallbacksProps } from '../Image/ImageWithFallbacks';
import { useTheme } from 'styled-components';

export interface ModalDialogProps {
  /** Optional. Size of the modal. Defaults to 'medium'  */
  size?: Size.Small | Size.Medium | Size.Large;
  /** Required. Current state of the modal. */
  isModalOpen: boolean;
  /** Optional. Content of the ModalDialog. */
  children?: React.ReactNode;
  /** Optional. Title of the ModalDialog. */
  title?: string | React.ReactNode;
  /** Optional. Content of the image to be shown in the header of the modal. */
  topImage?: any;
  /** Optional. Serves same purpose as 'topImage' except adds support for fallback options. Has higher priority than 'topImage'. */
  topImageWithFallbacksProps?: ImageWithFallbacksProps;
  /** Optional. If flag is set then overlay is added that converts image to grey color. */
  topImageGrayscale?: boolean;
  /** Optional. Collection of Buttons that will be shown in the footer on the right side of the ModalDialog. */
  buttons?: ButtonAction[];
  /** Optional. Left footer action that will be shown on the left side of the footer of ModalDialog. Can be a hyperlink, button or a note. */
  leftFooterAction?: LeftFooterAction;
  /** Optional. Tooltip that will be shown */
  tooltip?: string;
  /**  */
  backButton?: () => void;
  className?: string;
  id?: string;
  closeAction: () => void;
  hideCloseButton?: boolean;
  submitAction: (event?: React.FormEvent<HTMLFormElement> | undefined) => void;
  note?: string | React.ReactNode;
  state?: string;
  icon?: React.ReactNode;
  zIndex?: number;
  contentOverflow?: string;
  width?: string;
  hasContentBorders?: boolean;
  shouldCloseOnOverlayClick?: boolean;
}

export const ModalDialog: React.FunctionComponent<ModalDialogProps> = ({
  size,
  isModalOpen,
  title,
  topImage,
  topImageWithFallbacksProps,
  topImageGrayscale,
  buttons,
  leftFooterAction,
  backButton,
  closeAction,
  submitAction,
  tooltip,
  children,
  note,
  shouldCloseOnOverlayClick,
  state,
  icon,
  zIndex,
  contentOverflow,
  hideCloseButton,
  width,
  hasContentBorders,
  className,
  id,
}) => {

  const theme = useTheme();
  const getMinWidth = () => {
    switch (size) {
      case Size.Small:
        return '320px';
      case Size.Large:
        return '640px';
      case Size.Medium:
      default:
        return '480px';
    }
  };

  const getMaxWidth = () => {
    switch (size) {
      case Size.Small:
        return '480px';
      case Size.Large:
        return '720px';
      case Size.Medium:
      default:
        return '640px';
    }
  };

  const getImageHeight = () => {
    switch (size) {
      case Size.Small:
        return 160;
      case Size.Medium:
        return 200;
      case Size.Large:
        return 240;
      default:
        return 200;
    }
  };

  const getMarginBottom = () => {
    switch (size) {
      case Size.Small:
        return '16px';
      case Size.Medium:
        return '24px';
      case Size.Large:
        return '32px';
      default:
        return '24px';
    }
  };

  const getPadding = () => {
    switch (size) {
      case Size.Small:
        return '16px 16px 8px';
      case Size.Medium:
        return '24px 24px 20px';
      case Size.Large:
        return '32px';
      default:
        return '24px 24px 16px';
    }
  };

  const getMaxHeight = () => {
    switch (size) {
      case Size.Small:
        return 'calc(100vh - 56px)';
      case Size.Medium:
        return 'calc(100vh - 76px)';
      case Size.Large:
        return 'calc(100vh - 96px)';
      default:
        return 'calc(100vh - 72px)';
    }
  };

  const ModalTitle = (title: string | React.ReactNode, size?: Size) => {
    if (typeof title === 'string') {
      switch (size) {
        case Size.Small:
          return <ComponentM textStyle={ComponentTextStyle.Bold}>{title}</ComponentM>;
        case Size.Medium:
          return <ComponentL textStyle={ComponentTextStyle.Bold}>{title}</ComponentL>;
        case Size.Large:
          return <ComponentXL textStyle={ComponentTextStyle.Bold}>{title}</ComponentXL>;
        default:
          return <ComponentL textStyle={ComponentTextStyle.Bold}>{title}</ComponentL>;
      }
    } else {
      return title;
    }
  };

  const ModalTootip = (tooltip: string) => {
    return (
      <ModalHoverModifier>
        <TooltipWrapper delay="0s" size={Size.XSmall} align="center" position="top" withArrow={false} label={tooltip}>
          <IconButton variant="secondary" shape="circular" action={() => {}}>
            <SystemIcons.Help size="24px" color={COLORS.generateToken({componentType:'icon', defaultVariant:'subtle'}, theme)} />
          </IconButton>
        </TooltipWrapper>
      </ModalHoverModifier>
    );
  };

  const ModalCloseButton = (onClick: any) => {
    return (
      <CloseButtonWrapper $hasImage={!!topImage || !!topImageWithFallbacksProps} $size={size}>
        <IconButton variant="secondary" shape="circular" action={() => onClick()} borderRadius={48}>
          <SystemIcons.Close size="24px" color={COLORS.generateToken({componentType:'icon', defaultVariant:'subtle'}, theme)} />
        </IconButton>
      </CloseButtonWrapper>
    );
  };

  const ModalBackButton = () => {
    if (backButton) {
      return (
        <BackButtonWrapper $hasImage={!!topImage || !!topImageWithFallbacksProps} $size={size}>
          <IconButton variant="secondary" shape="circular" action={() => backButton()} borderRadius={48}>
            <SystemIcons.ArrowLineLeft size="24px" color={COLORS.generateToken({componentType:'icon', defaultVariant:'subtle'}, theme)} />
          </IconButton>
        </BackButtonWrapper>
      );
    }
  };

  const getLeftActionIconElement = (icon: React.ReactNode) => {
    return React.cloneElement(icon as React.ReactElement<any>, { size: size === Size.Small ? '20px' : size === Size.Large ? '28px' : '24px' });
  };

  const LeftFooterAction = (leftFooterAction: LeftFooterAction) => {
    const { text, actionType, disabled, icon, action } = leftFooterAction;
    switch (actionType) {
      case 'button':
        const button = leftFooterAction as LeftFooterButton;
        return (
          <TextButton
            id={'left-action-button'}
            type={button?.type}
            disabled={disabled}
            loading={button?.loading}
            icon={icon}
            size={size}
            onClick={action}
            variant={button?.variant ?? 'secondary'}
            style={{ order: -1, marginRight: 'auto' }}>
            {button?.children ?? button?.text}
          </TextButton>
        );
      case 'hyperlink':
        return (
          <HyperLink
            id="left-action-hyperlink"
            className="footer-action"
            href={(leftFooterAction as LeftFooterHyperlink).href}
            disabled={disabled}
            variant="default"
            onClick={(e) => {
              if (action) {
                e.preventDefault();
                action(e);
              }
            }}>
            {text}
            {icon && getLeftActionIconElement(icon)}
          </HyperLink>
        );
      case 'note':
        return (
          <div className="footer-action note">
            {icon && getLeftActionIconElement(icon)}
            <span>{text}</span>
          </div>
        );
    }
  };

  return (
    <ModalContainer
      className={className}
      id={id}
      showModal={isModalOpen}
      closeModal={closeAction}
      minWidth={getMinWidth()}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      maxWidth={getMaxWidth()}
      height="auto"
      padding={getPadding()}
      zIndex={zIndex}
      width={width}>
      <Column>
        <form onSubmit={submitAction} style={{ display: 'flex', flexDirection: 'column', maxHeight: getMaxHeight() }}>
          {(topImage || topImageWithFallbacksProps) && (
            <StyledModalHeader $size={size} $height={getImageHeight()} $marginBottom={getMarginBottom()}>
              <ImageContainer $height={getImageHeight()}>
                {topImageGrayscale && <ImageOverlay $height={getImageHeight()} />}
                {topImage && <img src={topImage} alt="Modal top" />}
                {!topImage && topImageWithFallbacksProps && (
                  <ImageWithFallbacks
                    loader={topImageWithFallbacksProps.loader}
                    alt={topImageWithFallbacksProps.alt}
                    fallbacks={topImageWithFallbacksProps.fallbacks}
                    src={topImageWithFallbacksProps.src}
                  />
                )}
              </ImageContainer>
              <ModalHeaderActionsWithImage $hasBackButton={!!backButton}>
                {ModalBackButton()}
                {!hideCloseButton && ModalCloseButton(closeAction)}
              </ModalHeaderActionsWithImage>
            </StyledModalHeader>
          )}
          <ModalTitleSection>
            <FlexContainer>
              {!(topImage || topImageWithFallbacksProps) && ModalBackButton()}
              {title && ModalTitle(title, size)}
            </FlexContainer>
            {(tooltip || !(topImage || topImageWithFallbacksProps)) && (
              <ModalHeaderActions>
                {tooltip && ModalTootip(tooltip)}
                {!(topImage || topImageWithFallbacksProps || hideCloseButton) && ModalCloseButton(closeAction)}
              </ModalHeaderActions>
            )}
          </ModalTitleSection>

          <ModalBody $size={size} $overflow={contentOverflow} $hasContentBorders={hasContentBorders}>
            {children}
          </ModalBody>

          {note && <ModalNote note={note} state={state} size={size} icon={icon} />}

          <ModalFooter $size={size}>
            {leftFooterAction && LeftFooterAction(leftFooterAction)}
            {buttons?.map((b, i) => (
              <TextButton key={b.id || i} icon={b.icon} id={b.id} disabled={b.disabled} loading={b.loading} size={size} onClick={b.action} type={b.type} variant={b.variant}>
                {b.children ?? b.text}
              </TextButton>
            ))}
          </ModalFooter>
        </form>
      </Column>
    </ModalContainer>
  );
};

export default ModalDialog;
